"use client";
export const dynamic = 'force-dynamic';

import { motion } from 'framer-motion';
import { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import Button from '@/components/ui/Button';
import { ArrowLeftIcon, HeartIcon, HomeIcon, ClockIcon, CurrencyDollarIcon, ShieldCheckIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface KittenInfo {
  id: string;
  name: string;
  age: string;
  color: string;
  gender: string;
  price: number;
  imageUrl: string;
  birthDate: string;
  readyDate: string;
  personality: string;
  status: string;
}

function ApplicationContent() {
  const searchParams = useSearchParams();
  const kittenId = searchParams?.get('kittenId') || null;
  const kittenName = searchParams?.get('kittenName') || null;
  const kittenImage = searchParams?.get('kittenImage') || null;
  const kittenAge = searchParams?.get('kittenAge') || null;
  const kittenColor = searchParams?.get('kittenColor') || null;
  const kittenGender = searchParams?.get('kittenGender') || null;
  const kittenPrice = searchParams?.get('kittenPrice') || null;
  const kittenPersonality = searchParams?.get('kittenPersonality') || null;

  const [selectedKitten, setSelectedKitten] = useState<KittenInfo | null>(null);
  const [formData, setFormData] = useState({
    // Personal Information
    kittenId: kittenId || '',
    kittenName: kittenName || '',
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    occupation: '',
    employer: '',
    workSchedule: '',

    // Address Information
    address: '',
    city: '',
    state: '',
    zipCode: '',
    housingType: '',
    landlordName: '',
    landlordPhone: '',
    moveInDate: '',

    // Household & Family
    householdType: '',
    hasChildren: '',
    childrenAges: '',
    hasOtherPets: '',
    otherPetsDetails: '',
    householdMembers: '',

    // Pet Experience
    previousCatExperience: '',
    ownedBreeds: '',
    veterinarianName: '',
    veterinarianPhone: '',
    veterinarianClinic: '',
    petInsurance: '',

    // Lifestyle & Daily Routine
    dailySchedule: '',
    travelFrequency: '',
    emergencyContact: '',
    emergencyPhone: '',
    backupCaregiver: '',
    backupPhone: '',

    // Financial Information
    annualIncome: '',
    petBudget: '',
    emergencyFund: '',
    understandsCosts: '',

    // Adoption Expectations
    whyThisKitten: '',
    timeCommitment: '',
    groomingCommitment: '',
    trainingExperience: '',
    allergenConsiderations: '',

    // References
    reference1Name: '',
    reference1Phone: '',
    reference1Relationship: '',
    reference2Name: '',
    reference2Phone: '',
    reference2Relationship: '',

    // Agreement & Consent
    backgroundCheck: '',
    homeVisit: '',
    socialMedia: '',
    agreeToTerms: false,
    signature: '',
    applicationDate: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  // Use real kitten data from URL parameters
  useEffect(() => {
    if (kittenId && kittenName) {
      const realKittenData: KittenInfo = {
        id: kittenId,
        name: kittenName,
        age: kittenAge || '12 weeks',
        color: kittenColor || 'Seal Point',
        gender: kittenGender || 'Unknown',
        price: kittenPrice ? parseInt(kittenPrice) : 2500,
        imageUrl: kittenImage || 'https://images.unsplash.com/photo-1574231164645-d6f0e8553590?auto=format&fit=crop&w=400&q=80',
        birthDate: '2024-01-15',
        readyDate: '2024-04-01',
        personality: kittenPersonality || 'Playful and affectionate with a gentle nature',
        status: 'Available'
      };
      setSelectedKitten(realKittenData);
    }
  }, [kittenId, kittenName, kittenAge, kittenColor, kittenGender, kittenPrice, kittenImage, kittenPersonality]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          applicationDate: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          // Reset form
          kittenId: '',
          kittenName: '',
          fullName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          occupation: '',
          employer: '',
          workSchedule: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          housingType: '',
          landlordName: '',
          landlordPhone: '',
          moveInDate: '',
          householdType: '',
          hasChildren: '',
          childrenAges: '',
          hasOtherPets: '',
          otherPetsDetails: '',
          householdMembers: '',
          previousCatExperience: '',
          ownedBreeds: '',
          veterinarianName: '',
          veterinarianPhone: '',
          veterinarianClinic: '',
          petInsurance: '',
          dailySchedule: '',
          travelFrequency: '',
          emergencyContact: '',
          emergencyPhone: '',
          backupCaregiver: '',
          backupPhone: '',
          annualIncome: '',
          petBudget: '',
          emergencyFund: '',
          understandsCosts: '',
          whyThisKitten: '',
          timeCommitment: '',
          groomingCommitment: '',
          trainingExperience: '',
          allergenConsiderations: '',
          reference1Name: '',
          reference1Phone: '',
          reference1Relationship: '',
          reference2Name: '',
          reference2Phone: '',
          reference2Relationship: '',
          backgroundCheck: '',
          homeVisit: '',
          socialMedia: '',
          agreeToTerms: false,
          signature: '',
          applicationDate: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, title: 'Personal Information', icon: UserGroupIcon },
    { id: 2, title: 'Home & Family', icon: HomeIcon },
    { id: 3, title: 'Pet Experience', icon: HeartIcon },
    { id: 4, title: 'Lifestyle & Finances', icon: ClockIcon },
    { id: 5, title: 'Expectations & References', icon: CurrencyDollarIcon },
    { id: 6, title: 'Agreement', icon: ShieldCheckIcon }
  ];

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCompletedSteps(prev => [...prev, currentStep]);
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <div className="max-w-6xl mx-auto px-4 py-12 sm:px-6 sm:py-16">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center mb-6">
            <Link
              href="/kittens"
              className="flex items-center text-warm-brown hover:text-soft-gold transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Kittens
            </Link>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-warm-brown mb-4">
            {selectedKitten ? `Application for ${selectedKitten.name}` : 'Kitten Application'}
          </h1>
          <p className="text-xl text-warm-brown/70 max-w-3xl mx-auto">
            Thank you for your interest in adopting one of our Balinese kittens. This comprehensive application helps us find the perfect match for both you and our kittens.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          className="bg-white/80 backdrop-blur rounded-2xl p-4 mb-8 border border-sand/20 sm:p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="grid grid-cols-3 gap-3 sm:gap-4 lg:grid-cols-6">
            {steps.map(step => {
              const Icon = step.icon;
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = step.id === currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 rounded-2xl border border-sand/20 px-3 py-3 transition-colors ${
                    isCompleted
                      ? 'bg-emerald-50'
                      : isCurrent
                      ? 'bg-soft-gold/15'
                      : 'bg-white/70'
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                      isCompleted
                        ? 'bg-emerald-100 text-emerald-600'
                        : isCurrent
                        ? 'bg-soft-gold text-warm-brown'
                        : 'bg-sand/20 text-warm-brown/40'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold uppercase tracking-wide text-warm-brown sm:hidden">
                      Step {step.id}
                    </span>
                    <span className="hidden text-sm font-medium leading-tight text-warm-brown sm:block">{step.title}</span>
                    <span
                      className={`hidden text-xs font-semibold sm:block ${
                        isCompleted
                          ? 'text-emerald-600'
                          : isCurrent
                          ? 'text-warm-brown'
                          : 'text-warm-brown/50'
                      }`}
                    >
                      {isCompleted ? 'Completed' : isCurrent ? 'In progress' : 'Pending'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
        <div className="flex flex-col-reverse gap-6 lg:grid lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)] lg:items-start">
          <div className="space-y-6">
            {submitStatus === 'success' && (
              <motion.div
                className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-3xl p-8 text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
                  <svg className="h-12 w-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="mb-4 text-2xl font-bold text-emerald-800">Application Submitted Successfully!</h3>
                <p className="mx-auto max-w-2xl text-lg text-emerald-700">
                  Thank you for your comprehensive application! Our team will carefully review all the information you've provided.
                  We'll be in touch within 2-3 business days with next steps in the adoption process.
                </p>
                <div className="mt-6 flex justify-center">
                  <Link
                    href="/kittens"
                    className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
                  >
                    Continue Browsing Kittens
                  </Link>
                </div>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-3xl p-8 text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                  <svg className="h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="mb-4 text-2xl font-bold text-red-800">Submission Error</h3>
                <p className="mb-6 mx-auto max-w-2xl text-lg text-red-700">
                  There was an error submitting your application. Please check your internet connection and try again.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </motion.div>
            )}

            {submitStatus === 'idle' && (
              <motion.form
                className="bg-white/90 backdrop-blur rounded-3xl shadow-xl space-y-8 border border-sand/20 p-6 sm:p-8"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >

            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">Personal Information</h2>
                  <p className="text-warm-brown/70">Help us get to know you and your background</p>
                </div>

                {selectedKitten && (
                  <div className="rounded-2xl border border-sand/30 bg-cream/30 p-4 sm:p-5">
                    <h3 className="font-heading text-xl font-semibold text-warm-brown">Kitten Details</h3>
                    <p className="text-sm text-warm-brown/70">These fields are pre-filled from the kitten you selected.</p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-warm-brown/80 mb-2">Kitten Name</label>
                        <input
                          type="text"
                          value={selectedKitten.name}
                          readOnly
                          className="w-full cursor-not-allowed rounded-xl border border-sand/30 bg-white/60 px-4 py-3 text-warm-brown shadow-inner"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-warm-brown/80 mb-2">Color</label>
                        <input
                          type="text"
                          value={selectedKitten.color}
                          readOnly
                          className="w-full cursor-not-allowed rounded-xl border border-sand/30 bg-white/60 px-4 py-3 text-warm-brown shadow-inner"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-warm-brown/80 mb-2">Age</label>
                        <input
                          type="text"
                          value={selectedKitten.age}
                          readOnly
                          className="w-full cursor-not-allowed rounded-xl border border-sand/30 bg-white/60 px-4 py-3 text-warm-brown shadow-inner"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-warm-brown/80 mb-2">Adoption Fee</label>
                        <input
                          type="text"
                          value={`$${selectedKitten.price.toLocaleString()}`}
                          readOnly
                          className="w-full cursor-not-allowed rounded-xl border border-sand/30 bg-white/60 px-4 py-3 text-warm-brown shadow-inner"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Enter your full legal name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="+1 (606) 465-2993"
                    />
                  </div>
                </div>
              </div>
            )}
            {currentStep === 2 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">Home & Family</h2>
                  <p className="text-warm-brown/70">Tell us about your living situation and household</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Full Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="State/Province"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">ZIP/Postal Code *</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="ZIP/Postal code"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Housing Type *</label>
                    <select
                      name="housingType"
                      value={formData.housingType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select housing type</option>
                      <option value="owned-house">Owned House</option>
                      <option value="rented-house">Rented House</option>
                      <option value="owned-apartment">Owned Apartment</option>
                      <option value="rented-apartment">Rented Apartment</option>
                      <option value="condo">Condo/Townhouse</option>
                      <option value="mobile-home">Mobile Home</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">How long have you lived at this address?</label>
                    <select
                      name="moveInDate"
                      value={formData.moveInDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select duration</option>
                      <option value="less-than-6-months">Less than 6 months</option>
                      <option value="6-months-1-year">6 months - 1 year</option>
                      <option value="1-2-years">1-2 years</option>
                      <option value="2-5-years">2-5 years</option>
                      <option value="5-plus-years">5+ years</option>
                    </select>
                  </div>
                </div>

                {(formData.housingType?.includes('rented') || formData.housingType?.includes('apartment')) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-amber-50/50 rounded-xl p-6 border border-amber-200/50">
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Landlord/Property Manager Name</label>
                      <input
                        type="text"
                        name="landlordName"
                        value={formData.landlordName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Property owner/manager name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Landlord Phone Number</label>
                      <input
                        type="tel"
                        name="landlordPhone"
                        value={formData.landlordPhone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Landlord contact number"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Household Type</label>
                    <select
                      name="householdType"
                      value={formData.householdType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select household type</option>
                      <option value="single">Single Person</option>
                      <option value="couple">Couple</option>
                      <option value="family-children">Family with Children</option>
                      <option value="family-no-children">Family without Children</option>
                      <option value="roommates">With Roommates</option>
                      <option value="multi-generational">Multi-Generational</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Do you have children living at home?</label>
                    <select
                      name="hasChildren"
                      value={formData.hasChildren}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes, living at home</option>
                      <option value="no">No children</option>
                      <option value="visiting">Children visit regularly</option>
                      <option value="expecting">Expecting a child</option>
                    </select>
                  </div>
                </div>

                {formData.hasChildren === 'yes' && (
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Children's Ages</label>
                    <input
                      type="text"
                      name="childrenAges"
                      value={formData.childrenAges}
                      onChange={handleInputChange}
                      placeholder="e.g., 3, 7, 12, 15"
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Do you currently have other pets?</label>
                    <select
                      name="hasOtherPets"
                      value={formData.hasOtherPets}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="planning">Planning to get other pets</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Total household members</label>
                    <input
                      type="number"
                      name="householdMembers"
                      value={formData.householdMembers}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Number of people living in household"
                    />
                  </div>
                </div>

                {formData.hasOtherPets === 'yes' && (
                  <div className="bg-blue-50/50 rounded-xl p-6 border border-blue-200/50">
                    <label className="block text-sm font-medium text-warm-brown mb-2">Current Pets Details</label>
                    <textarea
                      name="otherPetsDetails"
                      value={formData.otherPetsDetails}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Please describe your current pets: type, breed, age, gender, spay/neuter status, and how they get along with other animals..."
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Pet Experience */}
            {currentStep === 3 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">Pet Experience & Knowledge</h2>
                  <p className="text-warm-brown/70">Help us understand your experience with pets and knowledge of cat care</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Previous Cat Ownership Experience *</label>
                  <textarea
                    name="previousCatExperience"
                    value={formData.previousCatExperience}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Describe your previous experience with cats. Include: how many cats you've owned, breeds, how long you had them, and what happened to them..."
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Breeds you've owned</label>
                    <input
                      type="text"
                      name="ownedBreeds"
                      value={formData.ownedBreeds}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="List any cat breeds you've owned"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Do you have pet insurance?</label>
                    <select
                      name="petInsurance"
                      value={formData.petInsurance}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select</option>
                      <option value="yes-current">Yes, currently have it</option>
                      <option value="yes-planning">Planning to get it</option>
                      <option value="no-interested">No, but interested</option>
                      <option value="no-not-interested">No, not interested</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Current Veterinarian Name</label>
                    <input
                      type="text"
                      name="veterinarianName"
                      value={formData.veterinarianName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Your vet's name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Veterinarian Phone</label>
                    <input
                      type="tel"
                      name="veterinarianPhone"
                      value={formData.veterinarianPhone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Vet clinic phone number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Veterinarian Clinic Name</label>
                  <input
                    type="text"
                    name="veterinarianClinic"
                    value={formData.veterinarianClinic}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    placeholder="Name of the veterinary clinic"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Lifestyle & Finances */}
            {currentStep === 4 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">Lifestyle & Financial Responsibility</h2>
                  <p className="text-warm-brown/70">Help us ensure you can provide a stable, loving home</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Describe your typical daily schedule</label>
                  <textarea
                    name="dailySchedule"
                    value={formData.dailySchedule}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Walk us through a typical day - when you're home, work hours, exercise routine, etc. This helps us match you with a kitten that fits your lifestyle."
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">How often do you travel?</label>
                    <select
                      name="travelFrequency"
                      value={formData.travelFrequency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select travel frequency</option>
                      <option value="never-rarely">Never or very rarely</option>
                      <option value="few-times-year">A few times per year</option>
                      <option value="monthly">Once a month</option>
                      <option value="bi-weekly">Every 2 weeks</option>
                      <option value="weekly">Weekly</option>
                      <option value="frequently">Frequently (multiple times/month)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Annual Household Income Range</label>
                    <select
                      name="annualIncome"
                      value={formData.annualIncome}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select income range</option>
                      <option value="under-25k">Under $25,000</option>
                      <option value="25k-50k">$25,000 - $50,000</option>
                      <option value="50k-75k">$50,000 - $75,000</option>
                      <option value="75k-100k">$75,000 - $100,000</option>
                      <option value="100k-150k">$100,000 - $150,000</option>
                      <option value="150k-plus">$150,000+</option>
                      <option value="prefer-not-say">Prefer not to say</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Monthly Pet Care Budget</label>
                    <select
                      name="petBudget"
                      value={formData.petBudget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select budget range</option>
                      <option value="under-100">Under $100</option>
                      <option value="100-200">$100 - $200</option>
                      <option value="200-300">$200 - $300</option>
                      <option value="300-500">$300 - $500</option>
                      <option value="500-plus">$500+</option>
                      <option value="not-sure">Not sure yet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Emergency Veterinary Fund</label>
                    <select
                      name="emergencyFund"
                      value={formData.emergencyFund}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select emergency fund status</option>
                      <option value="yes-planned">Yes, planned for emergencies</option>
                      <option value="yes-savings">Yes, have emergency savings</option>
                      <option value="yes-credit">Yes, have credit available</option>
                      <option value="planning">Planning to set up</option>
                      <option value="no-plan">No current plan</option>
                    </select>
                  </div>
                </div>

                <div className="bg-purple-50/50 rounded-xl p-6 border border-purple-200/50">
                  <label className="block text-sm font-medium text-warm-brown mb-2">Do you understand the costs of owning a Balinese cat?</label>
                  <select
                    name="understandsCosts"
                    value={formData.understandsCosts}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                  >
                    <option value="">Select</option>
                    <option value="yes-fully">Yes, I understand all costs</option>
                    <option value="yes-mostly">Yes, I understand most costs</option>
                    <option value="somewhat">Somewhat understand</option>
                    <option value="need-more-info">Need more information</option>
                  </select>
                  <p className="text-sm text-warm-brown/60 mt-2">
                    Balinese cats require regular grooming, premium food, routine veterinary care, and may need professional grooming services.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Emergency Contact Name</label>
                    <input
                      type="text"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Person to contact in emergencies"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Emergency Contact Phone</label>
                    <input
                      type="tel"
                      name="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Emergency contact number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Backup Caregiver Name</label>
                    <input
                      type="text"
                      name="backupCaregiver"
                      value={formData.backupCaregiver}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Person who can care for your cat"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Backup Caregiver Phone</label>
                    <input
                      type="tel"
                      name="backupPhone"
                      value={formData.backupPhone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                      placeholder="Backup caregiver number"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Expectations & References */}
            {currentStep === 5 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">Adoption Expectations & References</h2>
                  <p className="text-warm-brown/70">Help us understand what you're looking for and verify your background</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-brown mb-2">Why are you interested in adopting {selectedKitten ? selectedKitten.name : 'a Balinese kitten'}? *</label>
                  <textarea
                    name="whyThisKitten"
                    value={formData.whyThisKitten}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="What specifically draws you to this particular kitten or the Balinese breed? What qualities are you looking for in a companion?"
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">How much time can you dedicate daily?</label>
                    <select
                      name="timeCommitment"
                      value={formData.timeCommitment}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select time commitment</option>
                      <option value="1-2-hours">1-2 hours per day</option>
                      <option value="2-4-hours">2-4 hours per day</option>
                      <option value="4-6-hours">4-6 hours per day</option>
                      <option value="6-plus-hours">6+ hours per day</option>
                      <option value="varies">Varies by day</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Are you comfortable with grooming requirements?</label>
                    <select
                      name="groomingCommitment"
                      value={formData.groomingCommitment}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select grooming comfort level</option>
                      <option value="very-comfortable">Very comfortable - experienced</option>
                      <option value="somewhat-comfortable">Somewhat comfortable - willing to learn</option>
                      <option value="not-comfortable">Not comfortable - need professional help</option>
                      <option value="need-education">Need education on requirements</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Do you have experience with cat training?</label>
                    <select
                      name="trainingExperience"
                      value={formData.trainingExperience}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select training experience</option>
                      <option value="extensive">Extensive experience</option>
                      <option value="some-experience">Some experience</option>
                      <option value="basic-knowledge">Basic knowledge</option>
                      <option value="no-experience">No experience but willing to learn</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-brown mb-2">Do you have any allergies?</label>
                    <select
                      name="allergenConsiderations"
                      value={formData.allergenConsiderations}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select allergy status</option>
                      <option value="no-allergies">No known allergies</option>
                      <option value="mild-allergies">Mild allergies to cats</option>
                      <option value="allergy-shots">Taking allergy shots/medication</option>
                      <option value="hypoallergenic-needed">Need hypoallergenic breed</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="font-heading text-xl font-semibold text-warm-brown">Personal References</h3>
                  <p className="text-sm text-warm-brown/70">Please provide contact information for 2 personal references (not family members)</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Reference 1 - Full Name</label>
                      <input
                        type="text"
                        name="reference1Name"
                        value={formData.reference1Name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Reference full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Reference 1 - Phone</label>
                      <input
                        type="tel"
                        name="reference1Phone"
                        value={formData.reference1Phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Reference phone number"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-warm-brown mb-2">Reference 1 - Relationship</label>
                      <input
                        type="text"
                        name="reference1Relationship"
                        value={formData.reference1Relationship}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="How do you know this person?"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Reference 2 - Full Name</label>
                      <input
                        type="text"
                        name="reference2Name"
                        value={formData.reference2Name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Reference full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Reference 2 - Phone</label>
                      <input
                        type="tel"
                        name="reference2Phone"
                        value={formData.reference2Phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Reference phone number"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-warm-brown mb-2">Reference 2 - Relationship</label>
                      <input
                        type="text"
                        name="reference2Relationship"
                        value={formData.reference2Relationship}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="How do you know this person?"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Agreement */}
            {currentStep === 6 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-3xl font-bold text-warm-brown mb-4">Agreement & Commitment</h2>
                  <p className="text-warm-brown/70">Final step - please review and confirm your commitment</p>
                </div>

                <div className="space-y-6">
                  <div className="bg-amber-50/50 rounded-xl p-6 border border-amber-200/50">
                    <label className="block text-sm font-medium text-warm-brown mb-2">Background Check Consent</label>
                    <select
                      name="backgroundCheck"
                      value={formData.backgroundCheck}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select</option>
                      <option value="yes-consent">Yes, I consent to a background check</option>
                      <option value="no-consent">No, I do not consent</option>
                      <option value="discuss-first">Would like to discuss first</option>
                    </select>
                    <p className="text-sm text-warm-brown/60 mt-2">
                      We may conduct background checks to ensure the safety of our kittens and verify information provided.
                    </p>
                  </div>

                  <div className="bg-blue-50/50 rounded-xl p-6 border border-blue-200/50">
                    <label className="block text-sm font-medium text-warm-brown mb-2">Home Visit</label>
                    <select
                      name="homeVisit"
                      value={formData.homeVisit}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select</option>
                      <option value="yes-welcome">Yes, I welcome a home visit</option>
                      <option value="yes-virtual">Yes, virtual visit acceptable</option>
                      <option value="discuss-options">Would like to discuss options</option>
                      <option value="no-visit">Prefer no home visit</option>
                    </select>
                    <p className="text-sm text-warm-brown/60 mt-2">
                      We typically conduct home visits to ensure the environment is suitable for our kittens.
                    </p>
                  </div>

                  <div className="bg-purple-50/50 rounded-xl p-6 border border-purple-200/50">
                    <label className="block text-sm font-medium text-warm-brown mb-2">Social Media Verification</label>
                    <select
                      name="socialMedia"
                      value={formData.socialMedia}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                    >
                      <option value="">Select</option>
                      <option value="yes-provide">Yes, I can provide social media</option>
                      <option value="no-social-media">I don't use social media</option>
                      <option value="private-accounts">My accounts are private</option>
                      <option value="discuss-alternatives">Would like to discuss alternatives</option>
                    </select>
                    <p className="text-sm text-warm-brown/60 mt-2">
                      We may ask for social media verification to confirm your identity and see how you interact with animals.
                    </p>
                  </div>

                  <div className="bg-sand/20 rounded-2xl p-6 border border-sand/30">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleInputChange}
                        required
                        className="mt-1 h-5 w-5 text-soft-gold focus:ring-soft-gold border-sand/30 rounded"
                      />
                      <div className="text-sm text-warm-brown">
                        <p className="font-medium mb-3">Comprehensive Adoption Agreement *</p>
                        <div className="text-warm-brown/70 space-y-2 text-xs leading-relaxed max-h-48 overflow-y-auto">
                          <p><strong>I understand and agree that:</strong></p>
                          <p>• Adopting a kitten is a 15-20 year commitment requiring daily care, attention, and financial responsibility</p>
                          <p>• Balinese cats require regular grooming, premium nutrition, and routine veterinary care</p>
                          <p>• I must provide a safe, indoor-only environment and protect the kitten from hazards</p>
                          <p>• I will maintain current vaccinations and provide annual veterinary examinations</p>
                          <p>• I will spay/neuter the kitten as specified in the adoption contract</p>
                          <p>• I will never declaw the kitten or allow any form of mistreatment</p>
                          <p>• I will contact LINDY Balinese immediately if I can no longer care for the kitten</p>
                          <p>• I understand the adoption fee is non-refundable once the kitten is in my possession</p>
                          <p>• I agree to allow follow-up contact to ensure the kitten's well-being</p>
                          <p>• I understand that providing false information may result in application denial</p>
                          <p>• I consent to background verification and reference checks as part of the adoption process</p>
                          <p>• I understand that LINDY Balinese reserves the right to deny any application</p>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Digital Signature *</label>
                      <input
                        type="text"
                        name="signature"
                        value={formData.signature}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white/50 backdrop-blur transition-all"
                        placeholder="Type your full name as signature"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-brown mb-2">Application Date</label>
                      <input
                        type="date"
                        name="applicationDate"
                        value={new Date().toISOString().split('T')[0]}
                        readOnly
                        className="w-full px-4 py-3 border border-sand/30 rounded-xl bg-sand/10 text-warm-brown/60 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
                <div className="flex flex-col gap-3 pt-8 border-t border-sand/20 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        onClick={prevStep}
                        variant="outline"
                        size="md"
                        className="w-full sm:w-auto"
                      >
                        Back
                      </Button>
                    )}
                    {currentStep < steps.length ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        variant="primary"
                        size="md"
                        className="w-full sm:w-auto"
                      >
                        Next Step
                      </Button>
                    ) : (
                      <motion.button
                        type="submit"
                        disabled={isSubmitting || !formData.agreeToTerms || !formData.signature}
                        className={`w-full rounded-xl px-8 py-3 font-semibold transition-all shadow-lg sm:w-auto ${
                          isSubmitting
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-soft-gold to-gold-dark text-warm-brown hover:from-soft-gold/90 hover:to-gold-dark/90'
                        }`}
                        whileHover={!isSubmitting && formData.agreeToTerms && formData.signature ? { scale: 1.02 } : {}}
                        whileTap={!isSubmitting && formData.agreeToTerms && formData.signature ? { scale: 0.98 } : {}}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center">
                            <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-warm-brown"></div>
                            Submitting Application...
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            Submit Application
                          </div>
                        )}
                      </motion.button>
                    )}
                  </div>
                  <Link
                    href="/kittens"
                    className="w-full rounded-xl border border-warm-brown/30 px-6 py-3 text-center text-warm-brown transition-colors hover:bg-warm-brown/5 sm:w-auto"
                  >
                    Cancel
                  </Link>
                </div>
              </motion.form>
            )}
          </div>

          {selectedKitten && (
            <motion.aside
              className="space-y-4 rounded-3xl border border-sand/20 bg-white/90 p-6 shadow-xl backdrop-blur sm:p-8 lg:sticky lg:top-28"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative mx-auto h-40 w-40 overflow-hidden rounded-3xl border-4 border-soft-gold/60 bg-white shadow-lg sm:h-48 sm:w-48">
                <Image
                  src={selectedKitten.imageUrl}
                  alt={selectedKitten.name}
                  fill
                  className="object-cover"
                  sizes="192px"
                />
              </div>
              <div className="text-center">
                <h2 className="font-heading text-2xl font-bold text-warm-brown">{selectedKitten.name}</h2>
                <p className="mt-1 text-sm text-warm-brown/70">Application reference</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm text-warm-brown">
                <div className="rounded-2xl bg-cream/40 p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-warm-brown/60">Age</p>
                  <p className="font-semibold">{selectedKitten.age}</p>
                </div>
                <div className="rounded-2xl bg-cream/40 p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-warm-brown/60">Color</p>
                  <p className="font-semibold">{selectedKitten.color}</p>
                </div>
                <div className="rounded-2xl bg-cream/40 p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-warm-brown/60">Gender</p>
                  <p className="font-semibold">{selectedKitten.gender}</p>
                </div>
                <div className="rounded-2xl bg-cream/40 p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-warm-brown/60">Fee</p>
                  <p className="font-semibold">${selectedKitten.price.toLocaleString()}</p>
                </div>
              </div>
              <div className="rounded-2xl border border-sand/30 bg-white/80 p-4 text-center text-sm text-warm-brown/80">
                <p className="italic">"{selectedKitten.personality}"</p>
              </div>
              <div className="rounded-2xl border border-soft-gold/40 bg-soft-gold/10 p-4 text-left text-xs text-warm-brown/70">
                <p className="font-semibold text-warm-brown">Application ID</p>
                <p className="truncate font-mono">{selectedKitten.id}</p>
              </div>
            </motion.aside>
          )}
        </div>
      </div>
    </main>
  );
}

export default function ApplicationPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading application...</div>}>
      <ApplicationContent />
    </Suspense>
  );
}
